import { type ReactNode, useEffect, useState } from 'react'
import type {
  DailyReportMetric,
  DailyReportResponse,
  DailyReportSourceKey,
} from 'api/generated'
import { Button } from 'components/ui/button'
import {
  AGGREGATION_LABELS,
  PERIODS,
  changePct,
  formatMetric,
  formatPeriod,
  formatRowDate,
} from 'helpers/daily-report'
import { ArrowRight, Loader } from 'lucide-react'
import {
  type OverviewQuery,
  fetchDailyReportOverview,
} from 'store/dailyReport/dailyReportSlice'
import { useAppDispatch, useAppSelector } from 'store/store'
import { AppVsWeb } from './AppVsWeb'
import { MetricTile } from './MetricTile'
import { MetricTrendChart, Sparkline } from './MetricTrendChart'
import { RenewalOutcomes } from './RenewalOutcomes'
import { RevenueByMonth } from './RevenueByMonth'
import { UpcomingRenewals } from './UpcomingRenewals'

type Preset = (typeof PERIODS)[number]['key']

// Daily rows up to a quarter; the year reads better by week
const queryFor = (preset: Preset): OverviewQuery => ({
  preset,
  granularity: preset === 'current_year' ? 'week' : 'day',
})

const USER_METRICS = ['new_users', 'dau', 'wau', 'mau', 'yau', 'total_users']
const SUBSCRIPTION_METRICS = [
  'revenue',
  'new_subscriptions',
  'recurring_charges',
  'cancelled_subscriptions',
  'net_new_subscriptions',
  'active_subscriptions',
  'active_customers',
]
// The headline metrics of each channel card; the first one drives the sparkline
const CHANNELS: { source: DailyReportSourceKey; metrics: string[] }[] = [
  { source: 'ga4', metrics: ['site_views', 'app_views', 'magazine_views'] },
  { source: 'meta', metrics: ['ig_views', 'ig_followers', 'fb_views'] },
  { source: 'tiktok', metrics: ['total_views', 'total_followers'] },
  {
    source: 'youtube',
    metrics: ['views', 'new_subscribers', 'total_subscribers'],
  },
  { source: 'admob', metrics: ['ios_revenue', 'android_revenue'] },
  { source: 'wordpress', metrics: ['articles_published', 'total_articles'] },
  { source: 'spotify', metrics: ['streams', 'total_followers'] },
  { source: 'linkedin', metrics: ['impressions', 'total_followers'] },
]

const pick = (report: DailyReportResponse | undefined, keys: string[]) =>
  keys
    .map((key) => report?.metrics.find((m) => m.key === key))
    .filter((m): m is DailyReportMetric => !!m)

const previousTitle = (report: DailyReportResponse) =>
  `Rispetto a ${formatPeriod(report.previous_period.from, report.previous_period.to)}`

const Tile = ({
  report,
  metric,
  selected,
  onSelect,
}: {
  report: DailyReportResponse
  metric: DailyReportMetric
  selected?: boolean
  onSelect?: () => void
}) => (
  <MetricTile
    label={metric.label}
    value={formatMetric(metric, report.summary[metric.key])}
    note={AGGREGATION_LABELS[metric.aggregation]}
    change={changePct(
      report.summary[metric.key],
      report.previous_summary[metric.key]
    )}
    changeTitle={previousTitle(report)}
    title={metric.description ?? undefined}
    selected={selected}
    onSelect={onSelect}
  />
)

const Section = ({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: ReactNode
}) => (
  <section className='space-y-3'>
    <div>
      <h2 className='text-lg font-semibold'>{title}</h2>
      {description && (
        <p className='text-sm text-muted-foreground'>{description}</p>
      )}
    </div>
    {children}
  </section>
)

/** Tiles of a source that pick the metric of the chart below them. */
const TilesWithTrend = ({
  report,
  keys,
  columns,
}: {
  report: DailyReportResponse
  keys: string[]
  columns: string
}) => {
  const metrics = pick(report, keys)
  const [selectedKey, setSelectedKey] = useState(keys[0])
  const selected = metrics.find((m) => m.key === selectedKey) ?? metrics[0]
  if (!selected) return null

  return (
    <>
      <div className={`grid grid-cols-2 gap-2 ${columns}`}>
        {metrics.map((metric) => (
          <Tile
            key={metric.key}
            report={report}
            metric={metric}
            selected={metric.key === selected.key}
            onSelect={() => setSelectedKey(metric.key)}
          />
        ))}
      </div>
      <div className='rounded-lg border p-4'>
        <h3 className='text-sm font-semibold'>{selected.label}</h3>
        <p className='text-xs text-muted-foreground'>
          {report.period.granularity === 'day'
            ? 'Valore per giorno'
            : `Per settimana: ${AGGREGATION_LABELS[selected.aggregation]} dei giorni`}
        </p>
        <div className='mt-3'>
          <MetricTrendChart
            rows={report.rows}
            metric={selected}
            granularity={report.period.granularity}
            height={220}
          />
        </div>
      </div>
    </>
  )
}

const ChannelCard = ({
  report,
  keys,
  onOpen,
}: {
  report: DailyReportResponse
  keys: string[]
  onOpen: () => void
}) => {
  const metrics = pick(report, keys)
  const empty = report.rows.length === 0

  return (
    <div className='flex flex-col rounded-lg border p-4'>
      <div className='flex items-center justify-between gap-2'>
        <h3 className='text-sm font-semibold'>{report.label}</h3>
        <Button
          variant='ghost'
          size='sm'
          className='h-7 px-2 text-xs'
          onClick={onOpen}
        >
          Dettaglio <ArrowRight className='h-3 w-3' />
        </Button>
      </div>
      {empty ? (
        <p className='mt-2 text-sm text-muted-foreground'>
          Nessun dato nel periodo.
        </p>
      ) : (
        <>
          <dl className='mt-2 space-y-1'>
            {metrics.map((metric) => (
              <div
                key={metric.key}
                className='flex items-baseline justify-between gap-2 text-sm'
              >
                <dt className='truncate text-muted-foreground'>
                  {metric.label}
                </dt>
                <dd className='font-semibold tabular-nums'>
                  {formatMetric(metric, report.summary[metric.key])}
                </dd>
              </div>
            ))}
          </dl>
          {metrics[0] && (
            <div className='mt-auto pt-2'>
              <Sparkline
                rows={report.rows}
                metric={metrics[0]}
                granularity={report.period.granularity}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

/** Daily report KPIs for one period: users, subscriptions and revenue, channels. */
export const ReportOverview = ({
  onOpenDetail,
}: {
  onOpenDetail: (source: DailyReportSourceKey) => void
}) => {
  const dispatch = useAppDispatch()
  const {
    overview,
    overviewLoading: loading,
    overviewError: error,
  } = useAppSelector((state) => state.dailyReport)
  const [preset, setPreset] = useState<Preset>('last_30_days')
  const ready = !loading && !error

  useEffect(() => {
    dispatch(fetchDailyReportOverview(queryFor(preset)))
  }, [dispatch, preset])

  const users = overview.backoffice
  const subscriptions = overview.recharge
  const lastDay = users?.rows.at(-1)?.date ?? subscriptions?.rows.at(-1)?.date

  return (
    <div className='space-y-8'>
      <div className='flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 p-3'>
        {PERIODS.map((period) => (
          <Button
            key={period.key}
            size='sm'
            variant={preset === period.key ? 'secondary' : 'ghost'}
            onClick={() => setPreset(period.key)}
          >
            {period.label}
          </Button>
        ))}
        <span className='ms-auto text-xs text-muted-foreground'>
          Report giornaliero: dati raccolti ogni notte
          {lastDay && `, fino al ${formatRowDate(lastDay, 'day')}`}. Le
          variazioni sono rispetto al periodo precedente di pari durata.
        </span>
      </div>

      {loading && <Loader className='h-5 w-5 animate-spin' />}
      {error && <p className='text-red-500'>Errore: {error}</p>}

      {ready && users && (
        <Section
          title='Utenti'
          description='Registrazioni e utenti attivi giorno per giorno.'
        >
          <TilesWithTrend
            report={users}
            keys={USER_METRICS}
            columns='sm:grid-cols-3 lg:grid-cols-6'
          />
        </Section>
      )}

      {/* Revenue by month and renewals don't follow the period: they stay
          mounted (and don't refetch) while the overview reloads */}
      <Section
        title='Abbonamenti e ricavi'
        description='Abbonamenti premium e ricavi da Recharge.'
      >
        {ready && subscriptions && (
          <TilesWithTrend
            report={subscriptions}
            keys={SUBSCRIPTION_METRICS}
            columns='sm:grid-cols-4 lg:grid-cols-7'
          />
        )}
        {ready && (subscriptions || overview.renewals) && (
          <RenewalOutcomes
            renewals={overview.renewals}
            recharge={subscriptions}
          />
        )}
        <RevenueByMonth />
        <UpcomingRenewals />
      </Section>

      {ready && (
        <>
          {overview.ga4 && (
            <Section
              title='Analytics: app e sito'
              description='Google Analytics separato per app e sito web (magazine escluso), la somma e la quota di ciascuno.'
            >
              <AppVsWeb report={overview.ga4} />
            </Section>
          )}

          <Section
            title='Canali'
            description='Sito, magazine, social e pubblicità.'
          >
            <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
              {CHANNELS.map(({ source, metrics }) => {
                const report = overview[source]
                return report ? (
                  <ChannelCard
                    key={source}
                    report={report}
                    keys={metrics}
                    onOpen={() => onOpenDetail(source)}
                  />
                ) : null
              })}
            </div>
          </Section>
        </>
      )}
    </div>
  )
}
